import {
  ChatSegment,
  hasServerId,
  IChatToolActivity,
  IChatUiMessage,
  toChatMemoryAction,
} from 'src/api/queries/chat/useChatConversationQueries';
import { ChatStreamEvent } from 'src/api/streamChat';

const isStreamingReply = (message: IChatUiMessage): boolean =>
  message.role === 'assistant' && !!message.streaming;

const updateReply = (
  overlay: IChatUiMessage[],
  update: (reply: IChatUiMessage) => IChatUiMessage,
): IChatUiMessage[] =>
  overlay.some(isStreamingReply)
    ? overlay.map((m) => (isStreamingReply(m) ? update(m) : m))
    : overlay;

const appendSegment = (
  overlay: IChatUiMessage[],
  segment: ChatSegment,
): IChatUiMessage[] =>
  updateReply(overlay, (reply) => ({
    ...reply,
    segments: [...reply.segments, segment],
  }));

const appendText = (segments: ChatSegment[], text: string): ChatSegment[] => {
  const last = segments[segments.length - 1];
  return last?.kind === 'text'
    ? [...segments.slice(0, -1), { kind: 'text', text: last.text + text }]
    : [...segments, { kind: 'text', text }];
};

const appendToolCall = (
  segments: ChatSegment[],
  entry: IChatToolActivity,
): ChatSegment[] => {
  const last = segments[segments.length - 1];
  return last?.kind === 'tools'
    ? [
        ...segments.slice(0, -1),
        { kind: 'tools', activity: [...last.activity, entry] },
      ]
    : [...segments, { kind: 'tools', activity: [entry] }];
};

const settleToolCall = (
  segments: ChatSegment[],
  callId: string,
  result: Partial<IChatToolActivity>,
): ChatSegment[] =>
  segments.map((segment) =>
    segment.kind === 'tools' &&
    segment.activity.some((entry) => entry.callId === callId)
      ? {
          ...segment,
          activity: segment.activity.map((entry) =>
            entry.callId === callId
              ? { ...entry, ...result, status: 'done' }
              : entry,
          ),
        }
      : segment,
  );

/** @registry Pure reducer that folds one chat stream event into the streaming overlay (the sent question and the reply being written); `usage`, `error`, and unknown events return the overlay unchanged. */
export const applyStreamEvent = (
  overlay: IChatUiMessage[],
  event: ChatStreamEvent,
): IChatUiMessage[] => {
  switch (event.type) {
    case 'start':
      return overlay.map((m) =>
        m.role === 'user' && !hasServerId(m)
          ? { ...m, id: event.userMessageId }
          : m,
      );
    case 'delta':
      return updateReply(overlay, (reply) => ({
        ...reply,
        segments: appendText(reply.segments, event.text),
      }));
    case 'tool_use':
      return updateReply(overlay, (reply) => ({
        ...reply,
        segments: appendToolCall(reply.segments, {
          callId: event.callId,
          name: event.name,
          status: event.status,
        }),
      }));
    case 'tool_result':
      return updateReply(overlay, (reply) => ({
        ...reply,
        segments: settleToolCall(reply.segments, event.callId, {
          rowCount: event.rowCount,
          truncated: event.truncated,
          isError: event.isError,
          auditId: event.auditId ?? undefined,
        }),
      }));
    case 'chart':
      return appendSegment(overlay, { kind: 'chart', chart: event.spec });
    case 'table':
      return appendSegment(overlay, { kind: 'table', table: event.spec });
    case 'memory':
      return appendSegment(overlay, {
        kind: 'memory',
        memory: event.memory,
        memoryAction: toChatMemoryAction(event.action),
      });
    case 'done':
      return updateReply(overlay, (reply) => ({
        ...reply,
        id: event.messageId,
        streaming: false,
        stopReason: event.stopReason,
        usedMemoryIds: event.usedMemoryIds,
      }));
    default:
      return overlay;
  }
};
